import EntityBase from '@/data/EntityBase';
import generateThunkReducers from '@/data/util/generateThunkReducers';
import Integration from '@/integrations/modules/go/store/models/integration';
import {
  fetchRipplingConfiguration,
  optimisticallyUpdateRipplingConfiguration,
} from '@/integrations/modules/rippling/actions/configuration';
import { fetchRippling } from '@/integrations/modules/rippling/actions/fetchRippling';
import { fetchRipplingPositions } from '@/integrations/modules/rippling/actions/fetchRipplingPositions';
import { fetchRipplingRequestTypes } from '@/integrations/modules/rippling/actions/fetchRipplingRequestTypes';
import { fetchRipplingUsers } from '@/integrations/modules/rippling/actions/fetchRipplingUsers';
import { optimisticallyUpdateRipplingPositions } from '@/integrations/modules/rippling/actions/saveRipplingPositions';
import { optimisticallyUpdateRipplingRequestTypes } from '@/integrations/modules/rippling/actions/saveRipplingRequestTypes';
import { optimisticallyUpdateRipplingUsers } from '@/integrations/modules/rippling/actions/saveRipplingUsers';
import { fetchRipplingWagesConfig, saveRipplingWages } from '@/integrations/modules/rippling/actions/saveRipplingWages';
import {
  ripplingPayrollRunsSlice,
  ripplingPayrollRunsSliceName,
} from '@/integrations/modules/rippling/store/payrollRun/reducer';
import RipplingPayrollRunsState from '@/integrations/modules/rippling/store/payrollRun/state';
import type {
  RipplingPositionsData,
  RipplingRequestTypesData,
  RipplingUsersData,
} from '@/integrations/modules/rippling/types';

import { createSlice } from '@reduxjs/toolkit';
import { Map } from 'immutable';

type RipplingConfigurationMap = Map<string, unknown> & {
  get(key: 'job_dimension_id'): string | null;
  get(key: 'account_job_dimension_id'): string | null;
  get(key: 'app_listing_id'): string;
  get(key: 'positions'): RipplingPositionsData;
  get(key: 'request_types'): RipplingRequestTypesData;
  get(key: 'users'): RipplingUsersData;
};

type RipplingStateValues = {
  integration: Integration;
  integrationLoaded: boolean;
  payrollRuns: RipplingPayrollRunsState;
  configurationLoaded: boolean;
  usersConfigLoaded: boolean;
  positionsConfigLoaded: boolean;
  requestTypesConfigLoaded: boolean;
  wagesConfigLoaded: boolean;
  wagesSyncWages: boolean;
  configuration: RipplingConfigurationMap;
};

const defaultConfiguration = Map({
  job_dimension_id: null as string | null,
  account_job_dimension_id: null as string | null,
  app_listing_id: '',
  positions: {} as RipplingPositionsData,
  request_types: {} as RipplingRequestTypesData,
  users: {} as RipplingUsersData,
}) as RipplingConfigurationMap;

export class RipplingIntegrationState extends EntityBase<undefined, RipplingStateValues>({
  integration: new Integration(),
  integrationLoaded: false,
  payrollRuns: new RipplingPayrollRunsState(),
  configurationLoaded: false,
  usersConfigLoaded: false,
  positionsConfigLoaded: false,
  requestTypesConfigLoaded: false,
  wagesConfigLoaded: false,
  wagesSyncWages: true,
  configuration: defaultConfiguration,
}) {}

const initialState = new RipplingIntegrationState();

export const ripplingIntegrationSlice = createSlice({
  name: 'rippling',
  initialState,
  reducers: {
    resetRipplingState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRippling.fulfilled, (state, action) => {
        return state.merge({
          integration: action.payload as Integration,
          integrationLoaded: true,
          error: null,
          loaded: true,
          loading: false,
          receivedAt: Date.now(),
        });
      })
      .addCase(fetchRippling.pending, state => {
        return state.merge({
          loaded: false,
          loading: true,
          receivedAt: 0,
        });
      })
      .addCase(fetchRippling.rejected, (state, action) => {
        return state.merge({
          error: action.payload,
          loaded: false,
          loading: false,
        });
      })
      .addCase(fetchRipplingConfiguration.fulfilled, (state, action) => {
        const data = action.payload.data;
        return state
          .set(
            'configuration',
            Map({
              job_dimension_id: data.job_dimension_id,
              account_job_dimension_id: data.account_job_dimension_id ?? null,
              app_listing_id: data.app_listing_id,
              positions: {
                job_dimension_id: data.positions.job_dimension_id,
                rippling_dimensions: Map(data.positions.rippling_dimensions),
                matched: Map(data.positions.matched).mapKeys(Number),
                rippling: Map(data.positions.rippling),
              },
              request_types: {
                matched: Map(data.request_types.matched).mapKeys(Number),
                rippling: Map(data.request_types.rippling),
              },
              users: {
                ignored: Map(data.users.ignored).mapKeys(Number),
                matched: Map(data.users.matched).mapKeys(Number),
                rippling: Map(data.users.rippling),
              },
            }) as RipplingConfigurationMap,
          )
          .merge({
            configurationLoaded: true,
            usersConfigLoaded: true,
            positionsConfigLoaded: true,
            requestTypesConfigLoaded: true,
          });
      })
      .addCase(optimisticallyUpdateRipplingConfiguration.fulfilled, (state, action) => {
        const data = action.payload;
        return state
          .set(
            'configuration',
            Map({
              job_dimension_id: data.job_dimension_id,
              account_job_dimension_id: data.account_job_dimension_id ?? null,
              app_listing_id: data.app_listing_id,
              positions: data.positions,
              request_types: data.request_types,
              users: data.users,
            }) as RipplingConfigurationMap,
          )
          .merge({
            configurationLoaded: true,
            usersConfigLoaded: true,
            positionsConfigLoaded: true,
            requestTypesConfigLoaded: true,
          });
      })
      .addCase(fetchRipplingUsers.fulfilled, (state, action) => {
        const data = action.payload.data;
        return state
          .setIn(['configuration', 'users'], {
            matched: Map(data.matched).mapKeys(Number),
            ignored: Map(data.ignored).mapKeys(Number),
            rippling: Map(data.rippling),
          })
          .set('usersConfigLoaded', true);
      })
      .addCase(fetchRipplingPositions.fulfilled, (state, action) => {
        const data = action.payload.data;
        return state
          .mergeIn(['configuration'], {
            account_job_dimension_id: data.account_job_dimension_id,
            job_dimension_id: data.job_dimension_id,
          })
          .setIn(['configuration', 'positions'], {
            matched: Map(data.matched).mapKeys(Number),
            rippling: Map(data.rippling),
            rippling_dimensions: Map(data.rippling_dimensions),
          })
          .set('positionsConfigLoaded', true);
      })
      .addCase(fetchRipplingRequestTypes.fulfilled, (state, action) => {
        const data = action.payload.data;
        return state
          .setIn(['configuration', 'request_types'], {
            matched: Map(data.matched).mapKeys(Number),
            rippling: Map(data.rippling),
          })
          .set('requestTypesConfigLoaded', true);
      })
      .addCase(optimisticallyUpdateRipplingUsers.fulfilled, (state, action) => {
        const data = action.payload;
        return state.setIn(['configuration', 'users'], {
          matched: data.matched,
          ignored: data.ignored,
          rippling: data.rippling,
        });
      })
      .addCase(optimisticallyUpdateRipplingPositions.fulfilled, (state, action) => {
        const data = action.payload;
        return state.setIn(['configuration', 'positions'], {
          job_dimension_id: data.job_dimension_id,
          matched: data.matched,
          rippling: data.rippling,
          rippling_dimensions: data.rippling_dimensions,
        });
      })
      .addCase(optimisticallyUpdateRipplingRequestTypes.fulfilled, (state, action) => {
        const data = action.payload;
        return state.setIn(['configuration', 'request_types'], {
          matched: data.matched,
          rippling: data.rippling,
        });
      })
      .addCase(fetchRipplingWagesConfig.fulfilled, (state, action) => {
        const data = action.payload.data;
        return state.merge({
          wagesConfigLoaded: true,
          wagesSyncWages: data.sync_wages,
        });
      })
      .addCase(saveRipplingWages.fulfilled, (state, action) => {
        return state.set('wagesSyncWages', action.meta.arg);
      });
    // Payroll Runs
    builder.addMatcher(
      action => action.type.startsWith(ripplingPayrollRunsSliceName),
      (state, action) =>
        state.set(
          'payrollRuns',
          ripplingPayrollRunsSlice.reducer(state.payrollRuns as RipplingPayrollRunsState, action),
        ),
    );
    generateThunkReducers(builder, 'ripplingIntegration');
  },
});

export const { resetRipplingState } = ripplingIntegrationSlice.actions;
export default ripplingIntegrationSlice.reducer;
